import { useAppStore } from '@src/app/context'
import { isWindowsDesktop } from '@src/lib/device'
import useReaction from '@src/lib/hooks/useReaction'
import type { ActiveCall } from '@src/service/voice'
import TwilioErrorCodes from '@src/service/voice/twilio/TwilioErrorCodes'

export default function useCallError(call: ActiveCall) {
  const { prompt, service } = useAppStore()

  useReaction(
    () => call.error,
    (error) => {
      if (!error) {
        return
      }

      const isWindows = isWindowsDesktop()

      if (
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- FIXME: Fix this ESLint violation!
        (!isWindows && error.code === TwilioErrorCodes.UserMediaFailError) ||
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- FIXME: Fix this ESLint violation!
        error.code === TwilioErrorCodes.GenericError
      ) {
        // If we have a date and it's a recent date, that means the refresh did not fix the issue so
        // the user will need to contact support
        if (
          call.rebootInitiatedDate &&
          new Date(call.rebootInitiatedDate) < new Date(Date.now() + 1000 * 60 * 5)
        ) {
          prompt.show({
            title: 'Error Connecting',
            body: `Unfortunately, the issue you're experiencing could not be solved through a manual refresh. Please contact our support team for assistance with troubleshooting (include the following error code to expedite the process: ${error.code})`,
            actions: [
              {
                title: 'Okay',
                type: 'primary',
                onClick: () => {
                  // eslint-disable-next-line react-compiler/react-compiler -- UXP-3732 - Fix React Compiler errors
                  call.rebootInitiatedDate = null
                },
              },
            ],
          })
        } else {
          prompt.show({
            title: 'Error Connecting',
            body: 'Oops, something went wrong. Please refresh the app so we can get you back up and running.',
            actions: [
              {
                title: 'Reload',
                type: 'primary',
                onClick: () => {
                  call.rebootInitiatedDate = new Date().toISOString()
                  window.location.reload()
                },
              },
            ],
          })
        }
      } else if (
        !service.flags.flags.webImprovedUserMediaDeviceAccess &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- FIXME: Fix this ESLint violation!
        error.code === TwilioErrorCodes.UserMediaFailError
      ) {
        prompt.show({
          title: 'Unable to access microphone',
          body: (
            <span>
              Please check your device settings to ensure your microphone is enabled and
              properly set up.{' '}
              <a
                href="https://support.openphone.com/hc/en-us/articles/24826367792919-Troubleshooting-outbound-call-errors-in-Windows#troubleshooting-outbound-call-errors-in-windows-0-0"
                target="_blank"
              >
                Troubleshooting guide
              </a>
            </span>
          ),
        })
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- FIXME: Fix this ESLint violation!
      } else if (error.code === TwilioErrorCodes.ICEConnectionFailError) {
        prompt.show({
          title: 'Connection Failed',
          body: (
            <span>
              OpenPhone was unable to establish a media connection. Check your device's
              network connection and try again. If the problem persists after a minute or
              two, contact{' '}
              <a
                href="https://support.openphone.co/hc/en-us/requests/new"
                target="_blank"
              >
                support
              </a>
              .
            </span>
          ),
        })
      } else if (
        !service.flags.flags.webImprovedUserMediaDeviceAccess &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- FIXME: Fix this ESLint violation!
        error.code === TwilioErrorCodes.PermissionDeniedError
      ) {
        prompt.show({
          title: 'Microphone Access',
          body: 'OpenPhone needs microphone access to start a call.',
          actions: [{ title: 'Okay', type: 'primary' }],
        })
      } else if (
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- FIXME: Fix this ESLint violation!
        error.code === TwilioErrorCodes.ConnectionError ||
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- FIXME: Fix this ESLint violation!
        error.code === TwilioErrorCodes.ConnectionDeclinedError
      ) {
        prompt.show({
          title: 'Error Connecting',
          body: (
            <span>
              We're sorry, an error occurred when connecting your call. Please try again.
            </span>
          ),
        })
      } else {
        prompt.show({
          title: 'Error Connecting',
          body: (
            <span>
              An error has occured, try again after a minute or two. If the problem
              persists, contact{' '}
              <a
                href="https://support.openphone.co/hc/en-us/requests/new"
                target="_blank"
              >
                support
              </a>
              .
            </span>
          ),
        })
      }
    },
    {},
    [call],
  )
}
